import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { EMPTY, type Observable, type ObservableInput, from, of, throwError } from "rxjs";
import { catchError, filter, finalize, scan, share, tap } from "rxjs/operators";

import { ProjectSelectedStore } from "@/stores/project-selected-store";
import type { RecoverableFeedKey } from "@/stores/recoverable-feed-store";
import { RecoverableFeedStore } from "@/stores/recoverable-feed-store";
import { WorkspaceContextStore } from "@/stores/workspace-context-store";
import { observableFromAbortableRequest } from "@/utils/map";
import { type AbortableRequest, type RequestResponse } from "@/utils/request";
import { loadInBatch } from "@/utils/request/batch_load";
import { getWorkOrderVisibilityGeneration } from "@/utils/request/work-order-visibility-context";
import { isCurrentWorkspaceResponse } from "@/utils/request/workspace-response-guard";
import {
  getRecoverableSuccessState,
  shareRecoverableObservable,
  type RecoverableFeedSurface,
  type RecoverableRequestState
} from "@/utils/recoverable-request-state";
import { scopeServiceQuery } from "@/utils/service-query";
import {
  canLoadForWorkspaceContext,
  getWorkspaceContextDependencyKey,
  shouldClearServiceForWorkspaceChange,
  type WorkspaceContextRequirement
} from "@/utils/workspace-context-gate";

import { isCurrentBatchRequest } from "./batchRequestLifecycle";
export type ScreenDimensions = {
  width: number;
  height: number;
  /** @property xs Screen smaller than 600px */
  xs: boolean;
  /** @property sm Screen smaller than 960px */
  sm: boolean;
  /** @property md Screen smaller than 1280px */
  md: boolean;
  /** @property l Screen smaller than 1920px */
  l: boolean;
  /** @property xl Screen bigger than 1920px */
  xl: boolean;
};

const safeDocument: Document = document;

export function useScreenDimensions(): ScreenDimensions {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    function updateSize() {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  return {
    width,
    height,
    xs: width <= 600,
    sm: width <= 960,
    md: width <= 1280,
    l: width <= 1920,
    xl: width > 1920
  };
}

export function setPageTitle(window: Window) {
  return (title?: string) => (): string => {
    window.document.title = title ? `${title} | Sitehub` : "Sitehub";
    return window.document.title;
  };
}
export function useGeoLocation():
  | { lat: number; lng: number }
  | null
  | undefined {
  const [geoLocation, setGeoLocation] = useState<{
    lat: number;
    lng: number;
  }>();
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (data) => {
        setGeoLocation({
          lat: data.coords.latitude,
          lng: data.coords.longitude
        });
      },
      (error) => {
        console.error(error);
        setGeoLocation({
          lat: 0,
          lng: 0
        });
      }
    );
  }, []);

  return geoLocation;
}
export function useObservable<Model>(
  $obs?: ObservableInput<Model>
): [Model | undefined] {
  const [value, setValue] = useState<Model>();
  const [$obsRef, set$ObsRef] = useState<Observable<Model>>();
  useEffect(() => {
    if ($obsRef || !$obs) return;
    set$ObsRef(from($obs));
  }, [$obs]);
  useEffect(() => {
    const subscription = $obsRef?.subscribe({
      next: setValue
    });
    return () => {
      subscription?.unsubscribe();
    };
  }, [$obsRef]);
  return [value];
}
export type BasicQuery = Record<
  string,
  string | number | boolean | undefined
> & {
  page?: number;
  workspace__id?: string | number;
  _reload?: boolean;
};

export function useService<Model, Query extends BasicQuery>(
  sourceFn: (query: Query) => AbortableRequest<Model>,
  initialQuery: Query,
  errorHandler: (error: any) => void = () => {},
  enabled: boolean = true,
  managedExternally: boolean = false,
  workspaceContextRequirement: WorkspaceContextRequirement = "operational",
  requestDependency?: unknown,
  clearOnUserContextRefresh: boolean = false,
  inFlightRequestKey?: (query: Query) => string
): [
  RequestResponse<Model> | undefined,
  Dispatch<SetStateAction<Query>>,
  Observable<RequestResponse<Model>> | undefined,
  Query
] {
  const [value, setValue] = useState<RequestResponse<Model>>();
  const [query, setQuery] = useState<Query>(initialQuery);
  const [observable, setObservable] =
    useState<Observable<RequestResponse<Model>>>();
  const [firstLoad, setFirstLoad] = useState(true);
  const [workspace] = useObservable(
    ProjectSelectedStore.getInstance().observable
  );
  const [workspaceContextReady] = useObservable(
    WorkspaceContextStore.getInstance().ready
  );
  const [workspaceContext] = useObservable(
    WorkspaceContextStore.getInstance().observable
  );
  const workspaceContextDependencyKey = getWorkspaceContextDependencyKey({
    requirement: workspaceContextRequirement,
    ready: workspaceContextReady === true,
    workspace,
    context: workspaceContext
  });
  const activeVisibilityGenerationRef = useRef(
    getWorkOrderVisibilityGeneration()
  );
  // Request already in flight for `inFlightRequestKey` callers (#1037).
  const inFlightRef = useRef<{ key: string; done: boolean } | null>(null);
  const [visibilityGeneration, setVisibilityGeneration] = useState(
    getWorkOrderVisibilityGeneration
  );

  useLayoutEffect(() => {
    if (!shouldClearServiceForWorkspaceChange(workspaceContextRequirement))
      return;
    setValue(undefined);
    setObservable(undefined);
  }, [workspace?.id, workspaceContextRequirement]);

  // Soft reload (`_reload`) must keep the previous value and must not clear
  // the observable here: clearing aborted the in-flight GET before the next
  // subscriber could join the shared request. The request effect below
  // replaces the observable when `query` changes; stale-while-revalidate of
  // `value` is already the default (only workspace-change clears it above).

  useEffect(() => {
    if (!clearOnUserContextRefresh) return;
    const clear = (): void => {
      const nextGeneration = getWorkOrderVisibilityGeneration();
      activeVisibilityGenerationRef.current = nextGeneration;
      setValue(undefined);
      setObservable(undefined);
      setVisibilityGeneration(nextGeneration);
    };
    window.addEventListener("sitehub:user-context-refreshed", clear);
    return () =>
      window.removeEventListener("sitehub:user-context-refreshed", clear);
  }, [clearOnUserContextRefresh]);

  useEffect(() => {
    const canLoad = canLoadForWorkspaceContext({
      requirement: workspaceContextRequirement,
      ready: workspaceContextReady === true,
      workspace,
      context: workspaceContext
    });
    if (!enabled || !canLoad) {
      setValue(undefined);
      setObservable(undefined);
      if (firstLoad) setFirstLoad(false);
      return;
    }
    if (firstLoad) {
      setFirstLoad(false);
      return;
    }
    if (
      workspaceContextRequirement !== "none" &&
      query.workspace__id === undefined &&
      !workspace?.id
    )
      return;
    const scopedQuery = scopeServiceQuery(
      query,
      workspace?.id,
      workspaceContextRequirement
    );
    const requestVisibilityGeneration = activeVisibilityGenerationRef.current;
    // A new query object asking for exactly what is still loading reuses
    // that request: re-sending it would abort it and ask again. Reloads
    // always go to the server.
    const inFlightKey = inFlightRequestKey
      ? JSON.stringify([
          inFlightRequestKey(scopedQuery as Query),
          workspaceContextDependencyKey,
          visibilityGeneration,
          requestDependency
        ])
      : null;
    if (
      inFlightKey !== null &&
      !scopedQuery._reload &&
      inFlightRef.current?.key === inFlightKey &&
      !inFlightRef.current.done
    )
      return;
    const inFlight =
      inFlightKey === null ? null : { key: inFlightKey, done: false };
    inFlightRef.current = inFlight;
    const localObservable = observableFromAbortableRequest(
      sourceFn(scopedQuery as Query)
    ).pipe(
      filter(
        () =>
          !clearOnUserContextRefresh ||
          activeVisibilityGenerationRef.current === requestVisibilityGeneration
      ),
      finalize(() => {
        if (inFlight) inFlight.done = true;
      }),
      // One execution for useService + page subscribers (e.g. zip). Without
      // share(), each subscriber registers the same AbortableRequest abort and
      // in-flight GET refcounting hard-aborts the only request (#3536).
      share()
    );
    setObservable(localObservable);
    // No need for sourceFn change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    firstLoad,
    enabled,
    workspaceContextDependencyKey,
    workspaceContextRequirement,
    visibilityGeneration,
    clearOnUserContextRefresh,
    requestDependency
  ]);

  useEffect(() => {
    if (!enabled) {
      if (firstLoad) setFirstLoad(false);
      return;
    }
    // Pages with recoverable request states own the only lifecycle subscription.
    if (managedExternally) return;
    if (!observable) return;
    const subscription = observable
      .pipe(
        share(),
        tap(
          () =>
            query._reload && setQuery((prev) => ({ ...prev, _reload: false }))
        ),
        catchError((err) => {
          errorHandler(err);
          return of(undefined);
        })
      )
      .subscribe({
        next: setValue
      });
    return () => subscription.unsubscribe();
  }, [observable, enabled, managedExternally]);

  return [value, setQuery, observable, query];
}

/**
 * Value-based dependency key for request headers. Callers commonly pass
 * `{ headers: { ... } }` as a literal, which is a new object on every render;
 * keying the request effect on its identity aborted and re-sent the same GET
 * in a loop (#3344). Only a real change of header names/values re-requests.
 */
export function getHeadersDependencyKey(
  headers?: Record<string, string>
): string {
  if (!headers) return "";
  return JSON.stringify(
    Object.entries(headers).sort(([a], [b]) => a.localeCompare(b))
  );
}

export function useServiceBatch<Model, Query extends BasicQuery>(
  baseURL: string,
  initialQuery: Query,
  errorHandler: (error: any) => void = () => {},
  options?: {
    bufferSize?: number;
    msDelay?: number;
    /** Extra request headers applied to every page of the batch. */
    headers?: Record<string, string>;
  },
  enabled: boolean = true,
  workspaceContextRequirement: WorkspaceContextRequirement = "operational",
  managedExternally: boolean = false,
  requestDependency?: unknown
): [
  Model[] | undefined,
  Dispatch<SetStateAction<Query>>,
  Observable<Model[]> | undefined,
  Query
] {
  const [value, setValue] = useState<Model[]>();
  const [query, setQuery] = useState<Query>(initialQuery);
  const [observable$, setObservable$] = useState<Observable<Model[]>>();
  const [workspace] = useObservable(
    ProjectSelectedStore.getInstance().observable
  );
  const [workspaceContextReady] = useObservable(
    WorkspaceContextStore.getInstance().ready
  );
  const [workspaceContext] = useObservable(
    WorkspaceContextStore.getInstance().observable
  );
  const workspaceContextDependencyKey = getWorkspaceContextDependencyKey({
    requirement: workspaceContextRequirement,
    ready: workspaceContextReady === true,
    workspace,
    context: workspaceContext
  });
  const activeWorkspaceIdRef = useRef(workspace?.id);
  activeWorkspaceIdRef.current = workspace?.id;
  const isWorkOrderBatch = /\/workorders\//.test(baseURL);
  const [visibilityGeneration, setVisibilityGeneration] = useState(
    getWorkOrderVisibilityGeneration
  );
  const activeBatchVisibilityGenerationRef = useRef(
    getWorkOrderVisibilityGeneration()
  );
  const batchRequestGenerationRef = useRef(0);

  useEffect(() => {
    if (!isWorkOrderBatch) return;
    const refresh = (): void => {
      const nextGeneration = getWorkOrderVisibilityGeneration();
      activeBatchVisibilityGenerationRef.current = nextGeneration;
      setValue(undefined);
      setObservable$(undefined);
      setVisibilityGeneration(nextGeneration);
    };
    window.addEventListener("sitehub:user-context-refreshed", refresh);
    return () =>
      window.removeEventListener("sitehub:user-context-refreshed", refresh);
  }, [isWorkOrderBatch]);

  useLayoutEffect(() => {
    batchRequestGenerationRef.current += 1;
    setValue(undefined);
    setObservable$(undefined);
  }, [query, workspace?.id, requestDependency]);
  const headersDependencyKey = getHeadersDependencyKey(options?.headers);

  useEffect(() => {
    const canLoad = canLoadForWorkspaceContext({
      requirement: workspaceContextRequirement,
      ready: workspaceContextReady === true,
      workspace,
      context: workspaceContext
    });
    if (!enabled || !canLoad) {
      setValue(undefined);
      setObservable$(undefined);
      return;
    }
    const requestWorkspaceId = workspace?.id ?? query.workspace__id;
    const requestVisibilityGeneration =
      activeBatchVisibilityGenerationRef.current;
    const requestGeneration = batchRequestGenerationRef.current;
    const localObservable = loadInBatch<Model>(
      baseURL,
      {
        ...query,
        page: query.page ?? 1,
        workspace__id: requestWorkspaceId
      },
      options?.bufferSize ?? 2,
      options?.msDelay ?? 1000,
      options?.headers
    ).pipe(
      filter(
        () =>
          isCurrentWorkspaceResponse(
            activeWorkspaceIdRef.current,
            requestWorkspaceId
          ) &&
          isCurrentBatchRequest({
            requestGeneration,
            currentGeneration: batchRequestGenerationRef.current,
            requestVisibilityGeneration,
            currentVisibilityGeneration:
              activeBatchVisibilityGenerationRef.current,
            visibilityScoped: isWorkOrderBatch
          })
      ),
      catchError((error) =>
        !isCurrentBatchRequest({
          requestGeneration,
          currentGeneration: batchRequestGenerationRef.current,
          requestVisibilityGeneration,
          currentVisibilityGeneration:
            activeBatchVisibilityGenerationRef.current,
          visibilityScoped: isWorkOrderBatch
        })
          ? EMPTY
          : throwError(() => error)
      ),
      share(),
      scan<Model[], Model[]>((acc, value) => {
        return Array.isArray(value) ? [...acc, ...value] : acc;
      }, [])
    );
    setObservable$(localObservable);
    //No need for exhaustive track
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    enabled,
    workspaceContextDependencyKey,
    workspaceContextRequirement,
    workspace?.id,
    visibilityGeneration,
    requestDependency,
    headersDependencyKey
  ]);

  useEffect(() => {
    if (!observable$) return;
    if (!enabled) return;
    if (managedExternally) return;
    const subscription = observable$.subscribe({
      next: setValue,
      error: (error) => {
        setValue(isWorkOrderBatch ? undefined : []);
        errorHandler(error);
      }
    });
    return () => {
      if (subscription.closed) return;
      subscription.unsubscribe();
    };
  }, [observable$, enabled, managedExternally]);

  return [value, setQuery, observable$, query];
}

type RecoverableHookOptions = {
  feed: RecoverableFeedKey;
  surface?: RecoverableFeedSurface;
};

function useRecoverableLifecycle<Model>(
  observable: Observable<Model> | undefined,
  errorHandler: (error: any) => void,
  options: RecoverableHookOptions,
  retry: () => void
) {
  const instanceId = useRef(
    `${options.feed}-${Math.random().toString(36).slice(2)}`
  );
  const errorHandlerRef = useRef(errorHandler);
  errorHandlerRef.current = errorHandler;
  const [value, setValue] = useState<Model>();
  const [requestState, setRequestState] = useState<RecoverableRequestState>({
    state: "loading",
    retry
  });

  useEffect(() => {
    const id = instanceId.current;
    const feedStore = RecoverableFeedStore.getInstance();
    feedStore.clear(id);
    setValue(undefined);
    setRequestState({ state: "loading", retry });
    if (!observable) return () => feedStore.clear(id);

    const subscription = observable.subscribe({
      next: (nextValue) => {
        feedStore.clear(id);
        setValue(nextValue);
        setRequestState({
          state: getRecoverableSuccessState(nextValue),
          retry
        });
      },
      error: (error) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const status = Number.isInteger(error?.status)
          ? Number(error.status)
          : undefined;
        setRequestState({ state: "error", status, retry });
        if (options.surface !== "primary" && status !== 403) {
          feedStore.report({
            id,
            feed: options.feed,
            status,
            retry
          });
        }
        errorHandlerRef.current(error);
      }
    });

    return () => {
      feedStore.clear(id);
      subscription.unsubscribe();
    };
  }, [observable, options.feed, options.surface, retry]);

  const safeObservable = useMemo(
    () => observable?.pipe(catchError(() => EMPTY)),
    [observable]
  );

  return { value, safeObservable, requestState };
}

export function useRecoverableService<Model, Query extends BasicQuery>(
  sourceFn: (query: Query) => AbortableRequest<Model>,
  initialQuery: Query,
  errorHandler: (error: any) => void,
  options: RecoverableHookOptions,
  enabled: boolean = true,
  workspaceContextRequirement: WorkspaceContextRequirement = "operational"
) {
  const [retryVersion, setRetryVersion] = useState(0);
  const [, setQuery, observable, query] = useService(
    sourceFn,
    initialQuery,
    () => undefined,
    enabled,
    true,
    workspaceContextRequirement,
    retryVersion
  );
  const retry = useCallback(
    () => setRetryVersion((current) => current + 1),
    []
  );
  const sharedObservable = useMemo(
    () => (observable ? shareRecoverableObservable(observable) : undefined),
    [observable]
  );
  const lifecycle = useRecoverableLifecycle(
    sharedObservable,
    errorHandler,
    options,
    retry
  );
  const requestState = useMemo(
    () => ({ ...lifecycle.requestState, retry }),
    [lifecycle.requestState, retry]
  );

  return [
    lifecycle.value,
    setQuery,
    lifecycle.safeObservable,
    query,
    requestState
  ] as const;
}

export function useRecoverableServiceBatch<Model, Query extends BasicQuery>(
  baseURL: string,
  initialQuery: Query,
  errorHandler: (error: any) => void,
  options: RecoverableHookOptions,
  batchOptions?: {
    bufferSize?: number;
    msDelay?: number;
    headers?: Record<string, string>;
  },
  enabled: boolean = true,
  workspaceContextRequirement: WorkspaceContextRequirement = "operational"
) {
  const [retryVersion, setRetryVersion] = useState(0);
  const [, setQuery, observable, query] = useServiceBatch<Model, Query>(
    baseURL,
    initialQuery,
    () => undefined,
    batchOptions,
    enabled,
    workspaceContextRequirement,
    true,
    retryVersion
  );
  const retry = useCallback(
    () => setRetryVersion((current) => current + 1),
    []
  );
  const sharedObservable = useMemo(
    () => (observable ? shareRecoverableObservable(observable) : undefined),
    [observable]
  );
  const lifecycle = useRecoverableLifecycle(
    sharedObservable,
    errorHandler,
    options,
    retry
  );
  const requestState = useMemo(
    () => ({ ...lifecycle.requestState, retry }),
    [lifecycle.requestState, retry]
  );

  return [
    lifecycle.value,
    setQuery,
    lifecycle.safeObservable,
    query,
    requestState
  ] as const;
}
export const useInitialRender = () => {
  const ref = useRef(true);
  useEffect(() => {
    ref.current = false;
  }, []);
  return ref.current;
};
export const usePageBottom = () => {
  const [reachedBottom, setReachedBottom] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsetHeight = document.documentElement.offsetHeight;
      const innerHeight = window.innerHeight;
      const scrollTop = document.documentElement.scrollTop;

      const hasReachedBottom = offsetHeight - (innerHeight + scrollTop) <= 10;

      setReachedBottom(hasReachedBottom);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return reachedBottom;
};

export const useOutsideAlerter = (
  ref: React.RefObject<HTMLDivElement>,
  triggerFunction: () => void
): void => {
  useEffect(() => {
    function handleClickOutside(event: Event) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        triggerFunction();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, triggerFunction]);
};

export const useScrollBlock = () => {
  const scrollBlocked = useRef<boolean>();
  const html = safeDocument.documentElement;
  const { body } = safeDocument;

  const blockScroll = () => {
    if (!body || !body.style || scrollBlocked.current) return;

    const scrollBarWidth = window.innerWidth - html.clientWidth;
    const bodyPaddingRight =
      parseInt(
        window.getComputedStyle(body).getPropertyValue("padding-right")
      ) || 0;

    html.style.position = "relative";
    html.style.overflow = "hidden";
    body.style.position = "relative";
    body.style.overflow = "hidden";
    body.style.paddingRight = `${bodyPaddingRight + scrollBarWidth}px`;

    scrollBlocked.current = true;
  };

  const allowScroll = () => {
    if (!body || !body.style || !scrollBlocked.current) return;

    html.style.position = "";
    html.style.overflow = "";
    body.style.position = "";
    body.style.overflow = "";
    body.style.paddingRight = "";

    scrollBlocked.current = false;
  };

  return [blockScroll, allowScroll];
};
